/** All consumers of a project's events share one room membership. */
import type { ProjectEventMessage } from '@/types/projectEvents'
import { PROJECT_EVENTS, projectEventId } from '@/utils/projectEvents'
import type { WebSocketClient } from '../client'
import { SubscriptionRegistry } from '../subscriptions'

export class ProjectLiveApi {
  private readonly subscriptions: SubscriptionRegistry<
    string,
    ProjectEventMessage
  >

  constructor(
    private readonly client: Pick<
      WebSocketClient,
      'attachOnce' | 'emitWhenConnected'
    >
  ) {
    this.subscriptions = new SubscriptionRegistry({
      join: (projectId) =>
        this.client.emitWhenConnected('subscribe_project', {
          project_id: projectId,
        }),
      leave: (projectId) =>
        this.client.emitWhenConnected('unsubscribe_project', {
          project_id: projectId,
        }),
    })
  }

  subscribe(
    projectId: string,
    callback: (message: ProjectEventMessage) => void = () => {}
  ): () => void {
    this.attachHandlers()
    return this.subscriptions.subscribe(projectId, callback)
  }

  private attachHandlers(): void {
    for (const type of PROJECT_EVENTS) {
      this.client.attachOnce(type, (data) => {
        const projectId = projectEventId(type, data)
        if (projectId) this.subscriptions.dispatch(projectId, { type, data })
      })
    }
  }

  replay(): void {
    this.attachHandlers()
    this.subscriptions.replay()
  }

  clear(): void {
    this.subscriptions.clear()
  }
}
